import { TimesheetPageEmployeesSettingFragment } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'
import { DeepPartial } from 'utility-types'

import { stringToDate } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

interface IAttendanceSetting {
  [key: string]: any
}

export const TYPES = {
  TOLERANCE_SETTING: 'tolerance_setting',
  BALANCE_CARRYOVER: 'balance_carryover',
}

export const ForgotClockOutOption = {
  CLOCK_OUT_REMINDER: 'clock_out_reminder',
  MISSING_CLOCK_OUT: 'missing_clock_out',
  DISABLED: 'disabled',
}

export const ExtraHoursBaselineOptions = {
  PLANNED_HOURS: 'planned_hours',
  CONTRACT_HOURS: 'contract_hours',
}

class AttendanceSetting extends Model<IAttendanceSetting> {
  constructor(attr: IAttendanceSetting) {
    super(attr)

    makeObservable(this, {
      policyId: computed,
      effectiveAt: computed,
      hasMobileClockin: computed,
      hasFlagsEnabled: computed,
      future: computed,
      hasBreaksEnabled: computed,
      hasLocationEnabled: computed,
    })
  }

  static fromFragment(object: DeepPartial<TimesheetPageEmployeesSettingFragment>) {
    const setting = {
      id: object.id,
      mobile_clockin: object.mobileClockin,
      desktop_clockin: object.desktopClockin,
      qr_code_clockin: object.qrCodeClockin,
      face_recognition: object.faceRecognition,
      id_clock_shared_device: object.idClockSharedDevice,
      overtime_authorization: object.overtimeAuthorization,
      edit_timesheet_authorization: object.editTimesheetAuthorization,
      autofill_timesheet: object.autofillTimesheet,
      enable_breaks: object.enableBreaks,
      enable_clock_in_location: object.enableClockInLocation,
      geofencing: object.geofencing,
      geolocation: object.geolocation,
      forgot_clock_out: object.forgotClockOut,
      payroll_compensation: object.payrollCompensation,
      timeoff_compensation: object.timeoffCompensation,
      extra_hours_baseline: object.extraHoursBaseline,
      complementary_hours_baseline: object.complementaryHoursBaseline,
      enable_complementary_hours: object.enableComplementaryHours,
      extra_hours_transfer_frequency: object.extraHoursTransferFrequency,
    }
    return new AttendanceSetting(setting)
  }

  get toGraphQLPayload() {
    return {
      id: this.attributes.id,
      balanceCarryoverId: this.attributes.balance_carryover_id,
      effectiveAt: this.attributes.effective_at,
      toleranceSettingId: this.attributes.tolerance_setting_id,
      attendanceEmployeesPolicyId: this.attributes.attendance_employees_policy_id,
      breakSettingId: this.attributes.break_setting_id,
      mobileClockin: this.attributes.mobile_clockin,
      desktopClockin: this.attributes.desktop_clockin,
      qrCodeClockin: this.attributes.qr_code_clockin,
      faceRecognition: this.attributes.face_recognition,
      idClockSharedDevice: this.attributes.id_clock_shared_device,
      overtimeAuthorization: this.attributes.overtime_authorization,
      editTimesheetAuthorization: this.attributes.edit_timesheet_authorization,
      autofillTimesheet: this.attributes.autofill_timesheet,
      enableBreaks: this.attributes.enable_breaks,
      enableClockInLocation: this.attributes.enable_clock_in_location,
      geofencing: this.attributes.geofencing,
      geolocation: this.attributes.geolocation,
      geolocationBlock: this.attributes.geolocation_block,
      forgotClockOut: this.attributes.forgot_clock_out,
      payrollCompensation: this.attributes.payroll_compensation,
      timeoffCompensation: this.attributes.timeoff_compensation,
      extraHoursBaseline: this.attributes.extra_hours_baseline,
      complementaryHoursBaseline: this.attributes.complementary_hours_baseline,
      enableComplementaryHours: this.attributes.enable_complementary_hours,
      extraHoursTransferFrequency: this.attributes.extraHoursTransferFrequency,
      publicToken: this.attributes.public_token,
      employeeAccessSearchField: this.attributes.employee_access_search_field,
    }
  }

  static urlRoot() {
    return '/attendance/employees_settings'
  }

  get id(): string {
    return this.attributes.id
  }

  get policyId() {
    return this.attributes.attendance_employees_policy_id
  }

  get breakSettingId(): string {
    return this.attributes.break_setting_id
  }

  get effectiveAt(): moment.Moment {
    const { effective_at } = this.attributes

    return effective_at ? stringToDate(effective_at, 'backend') : moment()
  }

  get hasMobileClockin(): boolean {
    return (
      this.attributes.mobile_clockin &&
      !this.attributes.qr_code_clockin &&
      !this.attributes.geolocation
    )
  }

  get hasFlagsEnabled(): boolean {
    return (
      this.hasMobileClockin ||
      this.attributes.desktop_clockin ||
      this.attributes.qr_code_clockin ||
      this.attributes.geolocation ||
      this.attributes.face_recognitiom ||
      this.attributes.id_clock_shared_device
    )
  }

  get future(): boolean {
    return this.effectiveAt.isAfter(moment(), 'day')
  }

  get hasOvertimeAuthorization(): boolean {
    return this.attributes.overtime_authorization
  }

  get hasEditTimesheetAuthorization(): boolean {
    return this.attributes.edit_timesheet_authorization
  }

  get hasAutofillTimesheet(): boolean {
    return this.attributes.autofill_timesheet
  }

  get hasBreaksEnabled(): boolean {
    return this.attributes.enable_breaks
  }

  get hasLocationEnabled(): boolean {
    return this.attributes.enable_clock_in_location
  }

  get hasGeofencingEnabled(): boolean {
    return this.attributes.geofencing
  }

  get hasGeolocationEnabled(): boolean {
    return this.attributes.geolocation
  }

  equals(settings: AttendanceSetting | undefined): boolean {
    return !!settings && this.attributes.id === settings.attributes.id
  }

  get forgotClockOutEnabled(): boolean {
    return this.attributes.forgot_clock_out !== ForgotClockOutOption.DISABLED
  }

  get missingClockOutEnabled(): boolean {
    return this.attributes.forgot_clock_out === ForgotClockOutOption.MISSING_CLOCK_OUT
  }

  get clockOutReminderEnabled(): boolean {
    return this.attributes.forgot_clock_out === ForgotClockOutOption.CLOCK_OUT_REMINDER
  }

  get anyCompensationEnabled(): boolean {
    return this.attributes.payroll_compensation || this.attributes.timeoff_compensation
  }

  get extraHoursBaseline(): string {
    return this.attributes.extra_hours_baseline
  }

  get extraHoursTransferFrequency(): string {
    return this.attributes.extra_hours_transfer_frequency
  }

  get complementaryHoursBaseline(): string | undefined {
    return this.attributes.complementary_hours_baseline
  }

  get enableComplementaryHours(): boolean {
    return this.attributes.enable_complementary_hours ?? false
  }

  get specialHoursRegularHoursCompensation(): boolean {
    return this.attributes.special_hours_regular_hours_compensation
  }

  get publicToken(): string {
    return this.attributes.public_token
  }
}

export default AttendanceSetting
